import type { SledTeam, TeamRepoScore } from '@sdd-maturity/shared-types';
import type { apiClient as ApiClient } from './api-client';
import type { RollupData } from './rollup';
import { ROOT_NUMBER } from './hierarchy-route';
import { isActiveRepo } from './utils';

/**
 * One row in the multi-team Standings table (FR-2). Each child node one level
 * below the current scope gets its Discovery and Delivery score shown side by
 * side — deliberately NOT blended into a single number (Non-Goal in the spec).
 */
export interface StandingsRow {
  node: SledTeam;
  /** 0–100 Discovery score, or null when the subtree has no scorable Discovery data. */
  discoveryScore: number | null;
  /** 0–100 Delivery score, or null when the subtree has no scored repos. */
  deliveryScore: number | null;
  /** tier-4 team count under this node (1 for a team row). */
  teamCount: number;
}

/** Average of a repo set's overallScore, mirroring TeamScoreHeader's aggregation. */
export function deliveryAvg(repos: TeamRepoScore[]): number | null {
  const scored = repos.filter(isActiveRepo);
  if (scored.length === 0) return null;
  return Math.round(scored.reduce((sum, r) => sum + r.overallScore, 0) / scored.length);
}

/**
 * Derive the Standings rows for a scope's direct children from a rollup ALREADY
 * fetched for that scope — pure, no fetch. A scope's own `getRollup` response
 * embeds a per-direct-child `leaderboard` (TASK-6 `buildLeaderboard`) whose `avg`
 * is BY CONSTRUCTION the same materialized value as that child's own rollup
 * aggregate (sweep.ts: "the SAME value that feeds this scope's own average") —
 * so a row's numbers are identical to that child's own scorecard without a
 * second read. This replaces the former per-child `getRollup` fan-out (one
 * `GetItem` × children) with zero extra calls: the ONE rollup fetch the caller
 * already made for the scope's own scorecard is reused here too. A child with
 * no scored units in a mode renders null (—, not a coerced 0).
 */
export function deriveStandings(children: SledTeam[], rollup: RollupData | null): StandingsRow[] {
  const deliveryByNumber = new Map(rollup?.delivery?.leaderboard?.map((r) => [r.unit.number, r]) ?? []);
  const discoveryByNumber = new Map(rollup?.discovery?.leaderboard?.map((r) => [r.unit.number, r]) ?? []);

  return children.map((child) => {
    const delivery = deliveryByNumber.get(child.number);
    const discovery = discoveryByNumber.get(child.number);
    return {
      node: child,
      discoveryScore: discovery && discovery.count > 0 ? discovery.avg : null,
      deliveryScore: delivery && delivery.count > 0 ? delivery.avg : null,
      teamCount: delivery?.unit.teamCount ?? discovery?.unit.teamCount ?? 0,
    };
  });
}

/**
 * Build a leaderboard-shaped `RollupData` for the ONE scope `deriveStandings` can't
 * derive from a parent fetch: the synthetic "whole org" root. `rollup-sweep` only
 * ever materializes REAL SLED hierarchy tiers (Portfolio/Stream/Train/Team) — the
 * root has no `AGG#`/`ROLLUP#` record of its own, so `getRollup(ROOT_NUMBER)` always
 * comes back an empty shell (`data-api`'s `rollupHandler` null-defaults gracefully to
 * a 200 with `leaderboard: []`, never a 404). Falling back to `deriveStandings` there
 * would silently render every Portfolio row "—" even though each Portfolio's own
 * rollup is real and materialized. So ONLY at root: fetch each top-level Portfolio's
 * own rollup directly (bounded by portfolio count, not total team count) and
 * reshape those into the same leaderboard shape `deriveStandings` already reads —
 * every other scope keeps the zero-extra-fetch path.
 */
let rootStandingsCache: { key: string; promise: Promise<RollupData>; expiresAt: number } | null = null;
const ROOT_STANDINGS_CACHE_TTL_MS = 60_000;

/**
 * Module-level single-flight + short-TTL cache for the root fan-out above.
 * Concurrent callers (e.g. React strict-mode's double-invoke, or Overview
 * re-rendering before the first fetch settles) share one in-flight promise
 * instead of each re-issuing `children.length` `getRollup` calls. Unlike
 * `getAllTeams`'s page-session-permanent cache, this one expires after
 * `ROOT_STANDINGS_CACHE_TTL_MS` — root rollups change whenever a scan
 * completes, and Overview's "Retry" button calls `refetch()` expecting a
 * real network hit, not a permanently-stale replay. Keyed by the child
 * portfolio numbers so a differently-scoped call (e.g. a tree edit, or a
 * test with a fresh mock) never serves another call's cached result. On
 * rejection the cache is cleared immediately so the next caller retries
 * against the network.
 */
export async function fetchRootStandingsRollup(
  children: SledTeam[],
  client: typeof ApiClient,
  token?: string,
): Promise<RollupData> {
  const now = Date.now();
  const key = children.map((c) => c.number).join(',');
  if (rootStandingsCache && rootStandingsCache.key === key && rootStandingsCache.expiresAt > now) {
    return rootStandingsCache.promise;
  }

  const promise = fetchRootStandingsRollupUncached(children, client, token).catch((err) => {
    rootStandingsCache = null;
    throw err;
  });
  rootStandingsCache = { key, promise, expiresAt: now + ROOT_STANDINGS_CACHE_TTL_MS };
  return promise;
}

/** Test-only escape hatch — clears the module-level cache above between cases. */
export function __resetRootStandingsCacheForTests(): void {
  rootStandingsCache = null;
}

async function fetchRootStandingsRollupUncached(
  children: SledTeam[],
  client: typeof ApiClient,
  token?: string,
): Promise<RollupData> {
  const perChild = await Promise.all(
    children.map(async (child) => ({ child, rollup: await client.getRollup(child.number, token).catch(() => null) })),
  );

  const leaderboardRow = (child: SledTeam, rollup: RollupData | null, mode: 'delivery' | 'discovery') => ({
    unit: { number: child.number, name: child.name, tier: child.tier, teamCount: rollup?.teamCount ?? 0 },
    avg: mode === 'delivery' ? rollup?.deliveryAvg ?? 0 : rollup?.discoveryAvg ?? 0,
    count: mode === 'delivery' ? rollup?.delivery.count ?? 0 : rollup?.discovery.count ?? 0,
    levelCounts: {},
  });
  const deliveryRows = perChild.map(({ child, rollup }) => leaderboardRow(child, rollup, 'delivery'));
  const discoveryRows = perChild.map(({ child, rollup }) => leaderboardRow(child, rollup, 'discovery'));

  // Same "average of present children's own scores" shape sweep.ts's
  // aggregateOverChildren uses server-side at every other tier — a Portfolio with
  // zero scored units this run doesn't drag the org average toward 0.
  const aggregate = (rows: Array<{ avg: number; count: number }>): { avg: number; count: number } => {
    const present = rows.filter((r) => r.count > 0);
    return {
      avg: present.length > 0 ? Math.round(present.reduce((sum, r) => sum + r.avg, 0) / present.length) : 0,
      count: rows.reduce((sum, r) => sum + r.count, 0),
    };
  };
  const deliveryAgg = aggregate(deliveryRows);
  const discoveryAgg = aggregate(discoveryRows);
  const teamCount = perChild.reduce((sum, { rollup }) => sum + (rollup?.teamCount ?? 0), 0);
  const repoCount = perChild.reduce((sum, { rollup }) => sum + (rollup?.repoCount ?? 0), 0);
  const featureCount = perChild.reduce((sum, { rollup }) => sum + (rollup?.featureCount ?? 0), 0);

  return {
    node: { number: ROOT_NUMBER, name: 'Cox Automotive' },
    teamCount,
    repoCount,
    featureCount,
    deliveryAvg: deliveryAgg.avg,
    discoveryAvg: discoveryAgg.avg,
    // No per-mode distribution to synthesize for this fabricated root shell (each
    // child Portfolio's OWN rollup already carries its real one) — empty here
    // renders the existing empty state (AC-1.4), never a fallback sum.
    delivery: { avg: deliveryAgg.avg, count: deliveryAgg.count, top10: [], leaderboard: deliveryRows, highlights: [], levelCounts: {} },
    discovery: { avg: discoveryAgg.avg, count: discoveryAgg.count, top10: [], leaderboard: discoveryRows, highlights: [], levelCounts: {} },
  };
}
